/**
 * @lines98/shared/util/player-label — derive stable, NON-PII player labels from a
 * player UUID (+ optional display_name).
 *
 * **Single source of truth** for how a player is shown by name across surfaces:
 *   - `playerSuffix(playerId)` — the short non-PII disambiguator (last 4 hex of the
 *     UUID). Shared by EVERY label below so a player's tag is identical everywhere.
 *   - `playerDisplayParts(displayName, playerId)` — the name AND the `#`-tag as
 *     SEPARATE strings: `{ name, tag }` (name = nick or «Игрок»; tag = the suffix).
 *     This is the source of truth the SERVER uses to send the leaderboard
 *     `player_name` (= name part) and `player_tag` (= suffix) fields SEPARATELY, so
 *     the client can style the nick at full size + the `#tag` smaller and muted
 *     (owner-decision 2026-06-09: tag reads as a subordinate `#`-discriminator, not
 *     part of the name). The suffix is ALWAYS present (spec §5.3 R-4: тёзки различимы
 *     без серверной проверки уникальности; единый визуальный язык с обезличенными
 *     лейблами — `Игрок` тоже несёт `#<суффикс>`).
 *   - `playerDisplayName(displayName, playerId)` — the JOINED «<ник> #<суффикс>»
 *     string, kept ONLY for surfaces that can't style two parts (e.g. the OG
 *     replay-card text). Uses the SAME `#` separator as the parts, for consistency.
 *   - `anonymousPlayerLabel(playerId)` — «Игрок #<суффикс>» joined fallback (no
 *     display_name; anon / OAuth-без-ника). Used by the OG card / replay page.
 *
 * Lives in `@lines98/shared` because BOTH server (leaderboard query / OG-card) AND
 * client (fallback rendering) need the identical derivation — no drift
 * (code-structure-conventions/SKILL.md: one cohesive concept, reused per surface).
 *
 * Deterministic + leaks nothing identifying (literal «Игрок» / «#» + the last 4 hex
 * chars of the UUID). Email is NEVER part of any label (PII, spec I-3).
 */
/**
 * Short, non-PII suffix from a player UUID — the last 4 hex chars (no dashes).
 * Shared by every label so all carry the SAME tag for a given player (spec §5.3).
 *
 * @example playerSuffix('…-0000000000a1'); // → '00a1'
 */
export function playerSuffix(playerId) {
    return playerId.replace(/-/g, '').slice(-4);
}
/**
 * The public name + `#`-tag as SEPARATE strings (spec §5.1 / §5.3, ADR rev3 §3).
 *
 * - `name` — the trimmed display_name if set, else the literal «Игрок».
 * - `tag` — `playerSuffix(playerId)` (last 4 hex). ALWAYS present (§5.3 R-4) so
 *   namesakes (uniqueness NOT required — ADR §3) stay distinguishable, and the
 *   anonymous «Игрок» reads with the same `#<tag>` visual language as a real nick.
 *
 * The SERVER calls this to put `player_name` (= `name`) and `player_tag` (= `tag`)
 * in leaderboard rows as DISTINCT fields, so the client can render the nick at full
 * size and the `#tag` smaller + muted (owner-decision 2026-06-09). Returning them
 * apart (not pre-joined) is what lets the UI style the tag as a subordinate
 * discriminator while keeping the value SERVER-formed (anti-cheat: client renders,
 * never forges).
 *
 * @param displayName — `players.display_name` (may be null/empty).
 * @param playerId — the owner UUID.
 * @returns `{ name, tag }` — e.g. `{ name: 'Андрей', tag: '00a1' }` or
 *   `{ name: 'Игрок', tag: '00a1' }`.
 */
export function playerDisplayParts(displayName, playerId) {
    const nick = (displayName ?? '').trim();
    // name = ник или ПУСТАЯ строка (когда ник не задан). Обезличенный плейсхолдер
    // «Игрок»/«Player»/… здесь БОЛЬШЕ НЕ запекаем по-русски: его рисует КЛИЕНТ на языке
    // зрителя из каталога i18n (owner-bug 2026-06-28 — на не-русском UI показывался русский
    // «Игрок»; сервер языка зрителя не знает). Модульно: один ключ в каталоге → любой новый
    // язык добавляется JSON-файлом без правок кода. Анти-чит цел: ЗАДАННЫЙ ник по-прежнему
    // server-formed (клиент не подделывает) — локализуется ровно заглушка «ник не задан».
    // SSR-склейки (`playerDisplayName` / `anonymousPlayerLabel`) держат литерал «Игрок» сами.
    return { name: nick, tag: playerSuffix(playerId) };
}
/**
 * Derive a brand-consistent, non-PII player label from a player UUID — the JOINED
 * «Игрок #<суффикс>» form, for surfaces that can't style two parts (OG card text).
 *
 * @param playerId — the owner UUID (with or without dashes).
 * @returns e.g. `'Игрок #00a1'`.
 *
 * @example
 *   anonymousPlayerLabel('00000000-0000-0000-0000-0000000000a1'); // → 'Игрок #00a1'
 */
export function anonymousPlayerLabel(playerId) {
    return `Игрок #${playerSuffix(playerId)}`;
}
/**
 * Resolve the JOINED public display name for a player (spec §5.1 / §5.3, ADR rev3 §3).
 *
 * - display_name set → `'<ник> #<суффикс>'` (suffix ALWAYS appended for tie-break
 *   between namesakes — §5.3 R-4; uniqueness is NOT required, ADR §3).
 * - display_name null / blank → the anonymous fallback `'Игрок #<суффикс>'`.
 *
 * Kept ONLY for surfaces that render the name as one styled-uniformly string (e.g.
 * the OG replay-card text, where the smaller-muted tag styling can't apply). The
 * leaderboard uses {@link playerDisplayParts} instead, to style the `#tag` apart.
 * Same `#` separator as the parts → no visual drift between the two surfaces.
 *
 * @param displayName — `players.display_name` (may be null/empty).
 * @param playerId — the owner UUID.
 */
export function playerDisplayName(displayName, playerId) {
    const { name, tag } = playerDisplayParts(displayName, playerId);
    // OG-карточку / replay-page рендерит СЕРВЕР (язык зрителя неизвестен) → для пустого ника
    // держим литерал «Игрок» прямо здесь, чтобы SSR-строка не зависела от клиентской локали.
    return `${name.length === 0 ? 'Игрок' : name} #${tag}`;
}
//# sourceMappingURL=player-label.js.map